import React from 'react';
import { useNavigate } from 'react-router-dom';
import { HyperlinkText, useTextToHyperlink } from './TextToHyperlink';
import { buildPersonDetailsUrl } from '../utils/personDetailsNavigation';

if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = `
    .vendor-name-link {
      color: inherit !important;
      text-decoration: none !important;
      cursor: pointer;
    }
    .vendor-name-link:hover {
      color: #000FFF !important;
      text-decoration: underline !important;
    }
  `;
  document.head.appendChild(style);
}

/** Vendor list names always open vendor detail. */
export const VendorNameHyperlink = ({ text, vendors = [], ...props }) => {
  const navigate = useNavigate();
  const { addListener, removeListener, instance, isReady } = useTextToHyperlink();
  const [forceUpdate, setForceUpdate] = React.useState(0);

  React.useEffect(() => {
    if (isReady) {
      setForceUpdate((prev) => prev + 1);
    }
  }, [isReady]);

  const navigateToVendorDetails = React.useCallback((personId) => {
    const url = buildPersonDetailsUrl(personId, { detailKind: 'vendor' });
    if (url) {
      navigate(url);
    }
  }, [navigate]);

  React.useEffect(() => {
    if (!isReady) return;

    vendors.forEach((vendor) => {
      if (vendor.name) {
        removeListener(vendor.name);
      }
    });

    vendors.forEach((vendor) => {
      if (vendor.name && vendor.person_id) {
        addListener(
          vendor.name,
          () => {
            navigateToVendorDetails(vendor.person_id);
          },
          {
            wholeWord: true,
            caseSensitive: true,
            className: 'vendor-name-link',
            style: {
              cursor: 'pointer',
              fontWeight: 'medium',
            },
          }
        );
      }
    });

    return () => {
      vendors.forEach((vendor) => {
        if (vendor.name) {
          removeListener(vendor.name);
        }
      });
    };
  }, [vendors, addListener, removeListener, isReady, forceUpdate, navigateToVendorDetails]);

  const handleClick = React.useCallback((e) => {
    if (!isReady || vendors.length === 0) return;

    const vendor = vendors[0];
    if (vendor?.name && vendor?.person_id) {
      e.preventDefault();
      navigateToVendorDetails(vendor.person_id);
    }
  }, [vendors, isReady, navigateToVendorDetails]);

  if (!isReady) {
    return (
      <span
        {...props}
        onClick={handleClick}
        style={{
          cursor: vendors.length > 0 ? 'pointer' : 'default',
          color: 'inherit',
          textDecoration: 'none',
        }}
      >
        {text}
      </span>
    );
  }

  return (
    <HyperlinkText
      text={text}
      hyperlinkInstance={instance}
      fallbackProps={{
        onClick: handleClick,
      }}
      {...props}
    />
  );
};

export default VendorNameHyperlink;
